import { Flex, Text } from "@chakra-ui/layout";
import {
	forwardRef,
	type Ref,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";
import SelectTab from "../components/inputs/SelectTab";
import SelectDateRange from "../components/inputs/SelectDateRange";
import { useAdminStats } from "../hooks/auth.hooks";
import ClientSideTable from "../components/table/ClientSideTable";
import {
	type ColumnDef,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import type { AdminStats } from "../types/auth.types";
import type { StatsHandler } from "./StatsPage";

type Intervals = "week" | "month" | "year";

const SupemboutUsersStatsPage = forwardRef(
	(_: unknown, ref: Ref<StatsHandler>) => {
		const [interval, setInterval] = useState<Intervals>("year");
		const [date, setDate] = useState<Date>(new Date());
		const { data: adminStats } = useAdminStats(interval, date);
		const columns = useMemo<ColumnDef<AdminStats>[]>(
			() => [
				{
					accessorFn: (row) => `${row.firstName} ${row.lastName}`,
					id: "user",
					header: "Utilisateur",
					cell: (info) => (
						<Text textTransform={"capitalize"}>
							{info.row.original.firstName} {info.row.original.lastName}
						</Text>
					),
					sortDescFirst: true,
					// enableGlobalFilter: true,
				},
				{
					accessorFn: (row) =>
						row.avgActionsPerDays && !isNaN(row.avgActionsPerDays)
							? `${row.avgActionsPerDays} j`
							: "--",
					id: "avgActionsPerDays",
					header: "Nombre d'embouts",
					cell: (info) => info.getValue(),
					sortDescFirst: true,
					enableGlobalFilter: false,
				},
				{
					accessorFn: () => <>--</>,
					id: "avgResponseTime",
					header: "Temps moyen de réponses",
					cell: (info) => info.getValue(),
					sortDescFirst: true,
					enableGlobalFilter: false,
				},
			],
			[],
		);
		const data = useMemo(() => adminStats ?? [], [adminStats]);
		const table = useReactTable({
			data: data,
			columns: columns,
			getCoreRowModel: getCoreRowModel(),
			getSortedRowModel: getSortedRowModel(),
			getPaginationRowModel: getPaginationRowModel(),
			getFilteredRowModel: getFilteredRowModel(),
		});
		useImperativeHandle(ref, () => ({
			exportAsCsv: () => {
				if (adminStats === undefined || adminStats === null) {
					return "";
				}
				return [
					"id,first_name,last_name,avg_actions_per_days,avg_response_time",
					...adminStats.map((stat) =>
						[
							stat.id,
							stat.firstName,
							stat.lastName,
							stat.avgActionsPerDays,
							stat.avgResponseTime,
						].join(","),
					),
				].join("\r\n");
			},
		}));

		return (
			<>
				<Flex justify="space-between" mt={8}>
					<SelectTab
						options={[
							{ label: "Semaine", value: "week" },
							{ label: "Mois", value: "month" },
							{ label: "Année", value: "year" },
						]}
						value={interval}
						setValue={(value) => {
							if (
								value !== undefined &&
								["week", "month", "year"].includes(value)
							) {
								setInterval(value as Intervals);
							}
						}}
					/>

					<Flex gap={4}>
						<SelectDateRange
							interval={interval}
							value={date}
							setValue={(date?: Date) => date && setDate(date)}
						/>
					</Flex>
				</Flex>

				<ClientSideTable
					table={table}
					w={["auto", "200px", "240px"]}
					flexGrow={[1, 0, 0]}
				/>
			</>
		);
	},
);

export default SupemboutUsersStatsPage;
